// App.js
import React, { useState, useEffect, createContext, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './services/AuthContext';
import HomeRadio from './pages/HomeRadio';
import RegisterModal from './fragments/RegisterModal';
import Login from './pages/Login';
import './App.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePresence } from './services/firebase';
import Monitoring from './pages/Monitoring';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { toast } from 'react-toastify';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from './services/firebase';

export const PresenceContext = createContext();

const NotFound = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="not-found-container">
      <div className="not-found-content">
        <h1>404</h1>
        <h2>Página no encontrada</h2>
        <p>La ruta que intentas acceder no existe o no tienes permisos para verla.</p>
        <button 
          onClick={handleLogout}
          className="not-found-logout-btn"
        >
          <i className="fas fa-sign-out-alt"></i>
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
};

function App() {
  const { user, login, logout } = useAuth();
  const { 
    clientName, 
    handleNameSubmit, 
    handleRadioChange, 
    isOnline, 
    getUsers, 
    registerInFirebase,
    updateOnlineStatus,
    clearPresence 
  } = usePresence(user ? user.server : null);
  const [radioData, setRadioData] = useState([]);
  const [isLoadingRadioData, setIsLoadingRadioData] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const isValidURL = (str) => {
    if (!str) return false;
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  // Función para obtener las rutas disponibles del usuario
  const getUserAvailableRoutes = useCallback(() => {
    if (!user) return [];

    try {
      // Si es nivel 0, tiene acceso a todas las rutas incluyendo especiales
      if (user.level === "0") {
        const routes = radioData.map(radio => {
          try {
            const url = new URL(radio.url_address);
            return url.pathname.substring(url.pathname.lastIndexOf('/') + 1).toLowerCase();
          } catch (error) {
            return null;
          }
        }).filter(Boolean);
        return [...routes, 'monitoreo', 'registro_usuario'];
      }

      // Si es usuario externo (tipo especial de nivel 2), acceso a todas las rutas sin monitoreo
      if (user.level === 'external') {
        return radioData.map(radio => {
          try {
            const url = new URL(radio.url_address);
            return url.pathname.substring(url.pathname.lastIndexOf('/') + 1).toLowerCase();
          } catch (error) {
            return null;
          }
        }).filter(Boolean);
      }

      // Para usuarios normales, necesitamos el servidor principal
      if (!user.server) return [];
      
      // Obtener ruta del servidor principal
      const userServerPath = new URL(user.server).pathname
        .substring(new URL(user.server).pathname.lastIndexOf('/') + 1)
        .toLowerCase();

      // Inicializar con el servidor principal
      let availableRoutes = [userServerPath];

      // Si es nivel 1, agregar rutas de servers_access y monitoreo
      if (user.level === "1") {
        if (user.servers_access && Array.isArray(user.servers_access)) {
          const additionalRoutes = user.servers_access.map(url => {
            try {
              const serverUrl = new URL(url);
              return serverUrl.pathname
                .substring(serverUrl.pathname.lastIndexOf('/') + 1)
                .toLowerCase();
            } catch (error) {
              return null;
            }
          }).filter(Boolean);
          availableRoutes = [...new Set([...availableRoutes, ...additionalRoutes])];
        }
        availableRoutes.push('monitoreo');
      }
      // Si es nivel 2, solo tiene acceso a su servidor principal
      else if (user.level === "2") {
        availableRoutes = [userServerPath];
      }

      return availableRoutes;
    } catch (error) {
      return [];
    }
  }, [user, radioData]);

  // Función para verificar si el usuario tiene acceso a una ruta específica
  const hasAccessToRoute = useCallback((pathName) => {
    if (!user || !pathName) return false;

    // Nivel 0 tiene acceso total
    if (user.level === "0") {
      return true;
    }

    const normalizedPathName = pathName.toLowerCase();
    const availableRoutes = getUserAvailableRoutes();

    const hasAccess = availableRoutes.includes(normalizedPathName);

    return hasAccess;
  }, [user, getUserAvailableRoutes]);

  // Verificar si la ruta existe en radioData
  const routeExists = useCallback((path) => {
    if (!path) return false;
    
    if (isLoadingRadioData) {
      return true;
    }
    
    if (!radioData || !radioData.length) {
      return false;
    }

    const normalizedPath = path.toLowerCase();

    const exists = radioData.some(radio => {
      if (!radio.url_address) return false;
      try {
        const radioPath = new URL(radio.url_address).pathname
          .substring(new URL(radio.url_address).pathname.lastIndexOf('/') + 1)
          .toLowerCase();
        return normalizedPath === radioPath;
      } catch (error) {
        return false;
      }
    });

    return exists;
  }, [radioData, isLoadingRadioData]);

  // Función para obtener datos de radio
  const fetchData = async () => {
    setIsLoadingRadioData(true);
    
    try {
      const response = await axios.get('https://radio.amgroupmkt.com:1030/api/v2/servers/');
      
      if (Array.isArray(response.data)) {
        const radioList = response.data.filter(radio => {
          if (!radio.url_address) {
            return false;
          }
          try {
            new URL(radio.url_address);
            return true;
          } catch (error) {
            return false;
          }
        });

        setRadioData(radioList);
        
        // Verificar que los datos se establecieron correctamente
        setTimeout(() => {
        }, 0);
      } else {
        setRadioData([]);
      }
    } catch (error) {
      toast.error('Error al cargar los datos de servidores');
      setRadioData([]);
    } finally {
      setIsLoadingRadioData(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Verificar acceso cuando cambia la ruta
  useEffect(() => {
    if (!user) {
      return;
    }

    // Si estamos cargando datos, esperar
    if (isLoadingRadioData) {
      return;
    }

    // Si es nivel 0, permitir navegación sin restricciones
    if (user.level === "0") {
      return;
    }

    // Si estamos en la ruta 404, detener la verificación
    if (location.pathname === '/404') {
      return;
    }

    // Obtener rutas disponibles y ruta actual
    const availableRoutes = getUserAvailableRoutes();
    const currentPath = location.pathname.split('/')[1].toLowerCase();

    // Si no hay ruta actual
    if (!currentPath) {
      // Para usuarios externos, redirigir al 404
      if (user.level === 'external' && radioData.length > 0) {
        navigate(`/amgroupradio`);
        return;
      }
      
      // Para otros usuarios, ir a su servidor principal
      if (user.server) {
        const userServerPath = new URL(user.server).pathname
          .substring(new URL(user.server).pathname.lastIndexOf('/') + 1)
          .toLowerCase();
        navigate(`/${userServerPath}`, { replace: true });
      }
      return;
    }

    // Verificar si la ruta existe
    if (!routeExists(currentPath)) {
      navigate('/404');
      return;
    }

    // Verificar si tiene acceso a la ruta
    if (!availableRoutes.includes(currentPath)) {
      // Si el usuario tiene un servidor principal asignado, redirigir ahí
      if (user.server) {
        const userServerPath = new URL(user.server).pathname
          .substring(new URL(user.server).pathname.lastIndexOf('/') + 1)
          .toLowerCase();
        
        // Solo mostrar mensaje y redirigir si no estamos ya en el servidor principal
        if (currentPath !== userServerPath) {
          const isMonitoreo = currentPath === 'monitoreo';
          const message = isMonitoreo ? 
            'No tienes acceso a monitoreo. Redirigiendo a tu servidor asignado' : 
            'No tienes acceso a esta ruta. Redirigiendo a tu servidor asignado';
          
          toast.error(message);
          navigate(`/${userServerPath}`, { replace: true });
        }
      } else {
        navigate('/404');
      }
      return;
    }
  }, [location, user, navigate, radioData, isLoadingRadioData, routeExists, getUserAvailableRoutes]);

  useEffect(() => {
    if (user) {
      handleNameSubmit(user.userId);
      const currentPath = location.pathname;
      const radioPath = currentPath.split('/')[1];
      
      // Para usuarios externos o con acceso a la ruta actual
      if (user.level === 'external' || (radioPath && hasAccessToRoute(radioPath))) {
        handleRadioChange(radioPath);
        registerInFirebase();
      }
    }
  }, [user, location.pathname, handleNameSubmit, handleRadioChange, registerInFirebase, hasAccessToRoute]);

  const handleLogout = useCallback(() => {
    if (window.stopRadioPlayer) {
      window.stopRadioPlayer();
    }
    const userName = JSON.parse(localStorage.getItem('user'))?.name;
    const radioPath = window.location.pathname.split('/')[1];
    if (userName && radioPath) {
      updateOnlineStatus(userName, radioPath).then(() => {
        setTimeout(() => {
          logout();
        }, 100);
      });
    } else {
      logout();
    }
  }, [logout, updateOnlineStatus]);

  useEffect(() => {
    window.handleLogout = handleLogout;
    return () => {
      delete window.handleLogout;
    };
  }, [handleLogout]);

  return (
    <PresenceContext.Provider value={{ 
      getUsers, 
      handleNameSubmit, 
      registerInFirebase, 
      updateOnlineStatus,
      handleLogout,
      handleRadioChange
    }}>
      <div className="App">
        {isLoading ? (
          <div className="loading-spinner">Cargando...</div>
        ) : user ? (
          <>
            <Routes>
              {/* Rutas para cada radio */}
              {radioData.map((radio, index) => {
                if (isValidURL(radio.url_address)) {
                  const url = new URL(radio.url_address);
                  const pathName = url.pathname
                    .substring(url.pathname.lastIndexOf('/') + 1)
                    .toLowerCase();
                  const hasAccess = hasAccessToRoute(pathName);
                  
                  return (
                    <Route
                      key={index}
                      path={`/${pathName}`}
                      element={
                        hasAccess ? (
                          <HomeRadio radio={radio} userLevel={user.level} />
                        ) : (
                          user.server ? (
                            (() => {
                              const userServerPath = new URL(user.server).pathname
                                .substring(new URL(user.server).pathname.lastIndexOf('/') + 1)
                                .toLowerCase();
                              return <Navigate to={`/${userServerPath}`} replace />;
                            })()
                          ) : (
                            <Navigate to="/404" replace />
                          )
                        )
                      }
                    />
                  );
                }
                return null;
              })}

              {/* Rutas especiales */}
              {user && user.level === "0" && (
                <Route path="/registro_usuario" element={<RegisterModal show={true} onClose={() => {}} />} />
              )}
              <Route path="/monitoreo" element={
                user.level === "0" || user.level === "1" ? (
                  <Monitoring show={true} onClose={() => {}} />
                ) : (
                  user.server ? (
                    (() => {
                      const userServerPath = new URL(user.server).pathname
                        .substring(new URL(user.server).pathname.lastIndexOf('/') + 1)
                        .toLowerCase();
                      return <Navigate to={`/${userServerPath}`} replace />;
                    })()
                  ) : (
                    <Navigate to="/404" replace />
                  )
                )
              } />
              
              {/* Ruta raíz y 404 */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={
                <Navigate to="/404" replace />
              } />

            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              style={{ zIndex: 9999 }}
            />
          </>
        ) : (
          <Login />
        )}
      </div>
    </PresenceContext.Provider>
  );
}

export default () => (
  <AuthProvider>
    <Router>
      <App />
    </Router>
  </AuthProvider>
);
